/**
 * Wishlist Hero bridge for dynamically inserted content.
 *
 * The Wishlist Hero app embed discovers `.wishlist-hero-custom-button` targets
 * present when its script initializes. Targets rendered later — the
 * recently-viewed slider, paginated or filtered collection grids, section
 * re-renders — must be announced with the `wishlist-hero-add-to-custom-element`
 * event. The live theme fulfilled this contract with the remote arrive.js
 * library; this module replaces it with a MutationObserver.
 */

const SELECTOR = '.wishlist-hero-custom-button';
const EVENT_NAME = 'wishlist-hero-add-to-custom-element';

// The app embed loads independently of this module, so an announcement made
// before it starts listening is lost. These sweeps re-announce any target the
// app has not filled yet. Bounded on purpose: when the embed is disabled the
// containers stay empty forever and an unbounded retry would never settle.
const SWEEP_DELAYS_MS = [0, 500, 1500, 3000];

/**
 * Announces a target to the app. A target the app has already rendered into is
 * skipped, which makes repeat announcements harmless — the emptiness check is
 * what keeps this idempotent, so no button is ever built twice.
 * @param {Element} element - The `.wishlist-hero-custom-button` container.
 */
function announce(element) {
  if (element.childElementCount > 0) return;
  document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: element }));
}

/**
 * Announces every unfilled target inside (or at) a root node.
 * @param {Node} root - A node that may contain wishlist targets.
 */
function scan(root) {
  if (!(root instanceof Element)) return;
  if (root.matches(SELECTOR)) announce(root);
  for (const element of root.querySelectorAll(SELECTOR)) announce(element);
}

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) scan(node);

    // A section morph can strip the app-rendered button out of a target that
    // stays in place (server HTML ships the container empty), so re-announce
    // the emptied container rather than waiting for a fresh insertion.
    const { target } = mutation;
    if (mutation.removedNodes.length > 0 && target instanceof Element && target.matches(SELECTOR)) {
      announce(target);
    }
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });

for (const delay of SWEEP_DELAYS_MS) {
  setTimeout(() => scan(document.body), delay);
}

// Theme-editor re-renders replace whole section subtrees.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) scan(event.target);
});
